import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, Plus, Pencil, Trash2, Loader2, Award, AlertOctagon, ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY = { name: "", category: "merit", value_type: "single", value: "1", min: "1", max: "5" };
const describeValue = (p) => (p.value_type === "range" ? `${p.min}–${p.max}` : `${p.value}`);

export default function CoachMeritOptions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [hasDistrict, setHasDistrict] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/merit-presets");
      setItems(data.items || []);
      setCanCreate(!!data.can_create);
      setHasDistrict(!!data.has_district_presets);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, category: p.category, value_type: p.value_type, value: String(p.value ?? 1), min: String(p.min ?? 1), max: String(p.max ?? 5) }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a preset name"); return; }
    const payload = { name: form.name.trim(), category: form.category, value_type: form.value_type };
    if (form.value_type === "single") payload.value = parseInt(form.value, 10);
    else { payload.min = parseInt(form.min, 10); payload.max = parseInt(form.max, 10); }
    setSaving(true);
    try {
      if (editing) { await api.put(`/coach/merit-presets/${editing.id}`, payload); toast.success("Preset updated"); }
      else { await api.post("/coach/merit-presets", payload); toast.success("Preset created"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/coach/merit-presets/${del.id}`); toast.success("Preset deleted"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const isOwn = (p) => p.created_by === "coach" && p.team_id;

  const Group = ({ title, list, Icon, tone }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Icon className={`h-4 w-4 ${tone}`} /> {title} <span className="text-slate-400">({list.length})</span></div>
      {list.length === 0 ? <p className="text-xs text-slate-400">No {title.toLowerCase()} presets.</p> : list.map((p) => (
        <Card key={p.id} className="flex items-center justify-between p-3" data-testid={`preset-row-${p.id}`}>
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${p.category === "merit" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{describeValue(p)}</span>
            <div>
              <p className="font-semibold text-slate-800">{p.name}</p>
              <p className="text-xs text-slate-400">{p.value_type === "range" ? `Range ${p.min}–${p.max}` : `Fixed ${p.value}`} · {isOwn(p) ? "Your team preset" : "District policy"}</p>
            </div>
          </div>
          {isOwn(p) ? (
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`preset-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDel(p)} data-testid={`preset-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ) : (
            <span className="flex items-center gap-1 text-xs text-slate-400" title="Set by your administrator"><Lock className="h-3 w-3" /> Policy</span>
          )}
        </Card>
      ))}
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate("/coach/merits")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="back-to-merits"><ArrowLeft className="h-4 w-4" /> Back to Merit System</button>
      <PageHeader title="Merit Options" subtitle="The merit & demerit presets available when you issue cases"
        actions={canCreate ? <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-preset-btn"><Plus className="mr-2 h-4 w-4" /> New preset</Button> : null} />

      {hasDistrict && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50 p-3 text-xs font-medium text-sky-700" data-testid="district-policy-note">
          <Lock className="h-4 w-4" /> Your administrator manages these presets for your district. Contact them to change the policy.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState title="No presets yet" description={canCreate ? "Add your own merit or demerit presets to speed up issuing cases." : "Your administrator hasn't set any merit presets for your team yet. You can still issue cases using \"Other (manual entry)\"."} testId="presets-empty" />
      ) : (
        <div className="grid gap-6 md:grid-cols-2" data-testid="presets-list">
          <Group title="Merits" list={items.filter((p) => p.category === "merit")} Icon={Award} tone="text-emerald-600" />
          <Group title="Demerits" list={items.filter((p) => p.category === "demerit")} Icon={AlertOctagon} tone="text-red-600" />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit preset" : "New preset"}</DialogTitle>
            <DialogDescription>This preset is available only to your team when issuing merit/demerit cases.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Tardy to practice" data-testid="preset-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="preset-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="merit">Merit</SelectItem><SelectItem value="demerit">Demerit</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Value type</Label>
                <Select value={form.value_type} onValueChange={(v) => setForm((f) => ({ ...f, value_type: v }))}>
                  <SelectTrigger data-testid="preset-valuetype-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Single number</SelectItem><SelectItem value="range">Range (min–max)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.value_type === "single" ? (
              <div className="space-y-2"><Label>Value</Label><Input type="number" min={1} max={100} value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} data-testid="preset-value-input" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Minimum</Label><Input type="number" min={1} max={100} value={form.min} onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))} data-testid="preset-min-input" /></div>
                <div className="space-y-2"><Label>Maximum</Label><Input type="number" min={1} max={100} value={form.max} onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))} data-testid="preset-max-input" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="preset-submit-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete preset?"
        description={del ? `"${del.name}" will be removed from your issue-case dropdown.` : ""}
        confirmText="Delete" destructive onConfirm={remove} testId="preset-delete-dialog" />
    </div>
  );
}
